import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Dialog, DialogHeader, DialogBody, DialogTitle } from "@/components/ui/dialog";
import { throwIfNotOk } from "@/lib/api-error";
import type { TestAssignment, TestQuestion, AnswerValue } from "@/types/test";

interface Props {
  assignmentId: string | null;
  onClose: () => void;
}

function renderAnswer(q: TestQuestion, a: AnswerValue | undefined, t: (k: string) => string): string {
  if (a === undefined) return t("tests.therapist.noAnswer");
  if (q.type === "short_text" || q.type === "long_text") {
    return Array.isArray(a) ? "" : a;
  }
  if (q.type === "single_choice" || q.type === "likert") {
    if (Array.isArray(a)) return "";
    const opts = "options" in q ? q.options : [];
    return opts.find((o) => o.value === a)?.label ?? a;
  }
  if (!Array.isArray(a)) return "";
  const opts = "options" in q ? q.options : [];
  return a.map((v) => opts.find((o) => o.value === v)?.label ?? v).join(", ");
}

export function AssignmentResultDialog({ assignmentId, onClose }: Props) {
  const { t } = useTranslation();
  const { data } = useQuery({
    enabled: Boolean(assignmentId),
    queryKey: ["therapist", "test-assignments", assignmentId],
    queryFn: async () => {
      const res = await fetch(`/api/test-assignments/${assignmentId}`);
      await throwIfNotOk(res, t("tests.therapist.fetchResultError"));
      return res.json() as Promise<TestAssignment>;
    },
  });

  const ratio =
    data?.score !== null && data?.score !== undefined && (data?.snapshotMaxScore ?? 0) > 0
      ? Math.min(1, Math.max(0, data.score / data.snapshotMaxScore))
      : null;

  return (
    <Dialog open={Boolean(assignmentId)} onClose={onClose} className="sm:max-w-2xl">
      <DialogHeader onClose={onClose}>
        {data && (
          <div className="space-y-1.5">
            <DialogTitle className="text-2xl font-medium">{data.snapshotName}</DialogTitle>
            <p className="text-[13px] italic text-muted-foreground">
              {data.clientFirstName} {data.clientLastName}
            </p>
          </div>
        )}
      </DialogHeader>
      <DialogBody>
        {data && (
          <div className="space-y-6">
            {data.score !== null && (
              <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.06] to-transparent p-6">
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,oklch(82.8%_0.189_84.429/0.12),transparent_60%)]"
                />
                <div className="relative flex flex-wrap items-end justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-primary/70">
                      {t("tests.therapist.score")}
                    </p>
                    <div className="flex items-baseline gap-2 leading-none">
                      <span
                        className="font-heading text-6xl font-medium italic tabular-nums tracking-tight text-foreground"
                        style={{ fontFamily: "var(--font-heading)" }}
                      >
                        {data.score}
                      </span>
                      <span className="font-heading text-2xl italic tabular-nums text-muted-foreground/70">
                        / {data.snapshotMaxScore}
                      </span>
                    </div>
                    {data.interpretationLabel && (
                      <p
                        className="pt-2 font-heading text-base italic tracking-tight text-foreground/80"
                        style={{ fontFamily: "var(--font-heading)" }}
                      >
                        “{data.interpretationLabel}”
                      </p>
                    )}
                  </div>
                  {ratio !== null && (
                    <div className="w-40 shrink-0 space-y-1.5">
                      <div className="flex justify-between text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                        <span>0</span>
                        <span className="tabular-nums">{Math.round(ratio * 100)}%</span>
                        <span>{data.snapshotMaxScore}</span>
                      </div>
                      <div className="relative h-1.5 overflow-hidden rounded-full bg-border/70">
                        <span
                          className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width]"
                          style={{ width: `${ratio * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                Answers
              </p>
              <ol className="space-y-2.5">
                {data.snapshotQuestions.map((q, idx) => (
                  <li
                    key={q.id}
                    className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg border border-border/70 bg-card p-4"
                  >
                    <span
                      className="font-heading text-sm italic tabular-nums leading-tight text-muted-foreground/70"
                      style={{ fontFamily: "var(--font-heading)" }}
                    >
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-tight">{q.text}</p>
                      <p className="mt-1 break-words text-[13px] italic text-muted-foreground">
                        {renderAnswer(q, data.answers?.[q.id], t)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}
